import { Box, Center, Spinner, Image } from '@chakra-ui/react'
import type { MutableRefObject, ReactElement } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { UserContext } from '../../../providers'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import { ThreadAvatarOverlay } from '../Details/Threads'
import { PermissionsEnum, checkOperationPermission } from '../../../../utils'
import { ACLPermissionEnum } from '../../../constants'
import PermissionGuard from '../../PermissionGuard'
import type {
  FileDetailsResponse,
  MediaLibrarySearchFileResponse,
} from '../../../interfaces'
import LazyNewThreadPanel from '../Details/Threads/LazyNewThreadPanel'
import type { FilePreviewProps } from '.'

const AudioPreview = ({
  file,
  showThreads = false,
  showPreview = false,
}: FilePreviewProps): ReactElement => {
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { thumbnail, url } = file
  const previewUrl = (file as FileDetailsResponse).previewUrl
  const [loading, setLoading] = useState<boolean>(true)
  const { user } = useContext(UserContext)

  const audioRef:
    | MutableRefObject<HTMLAudioElement>
    | MutableRefObject<undefined> = useRef()

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('loadeddata', event => {
        setLoading(false)
      })
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('loadeddata', event => {
          setLoading(false)
        })
      }
    }
  }, [])

  return (
    <>
      {loading && (
        <Center>
          <Spinner />
        </Center>
      )}
      <Box position="relative">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        {showPreview ? (
          <audio
            controls
            src={previewUrl ?? url}
            hidden={loading}
            ref={audioRef as MutableRefObject<HTMLAudioElement>}
          >
            Audio playback not supported by browser
          </audio>
        ) : (
          <Image src={thumbnail} boxSize="100%" padding="0" />
        )}
        {showThreads && !isPublicLink ? (
          <>
            <PermissionGuard
              permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
            >
              {(hasPermission: boolean) =>
                hasPermission &&
                checkOperationPermission({
                  user,
                  aclPermission: [
                    ACLPermissionEnum.CONTRIBUTE,
                    ACLPermissionEnum.MANAGE,
                  ],
                  selectedFiles: [file] as MediaLibrarySearchFileResponse[],
                  selectedFolders: [],
                  selectedMediaCollections: [],
                }) ? (
                  <LazyNewThreadPanel
                    fileDetails={file}
                    position={null}
                    setThreadPanelPosition={() => {}}
                  />
                ) : (
                  <></>
                )
              }
            </PermissionGuard>
            <ThreadAvatarOverlay fileDetails={file} />
          </>
        ) : null}
      </Box>
    </>
  )
}

export default AudioPreview
